import { Fragment, ReactNode } from 'react';

import { useScreenWidth } from '@/app/shared/hooks/useScreenWidth';
import { cn } from '@/app/shared/lib/cn';
import { dataTableValueClassName, isEmptyTableValue } from '@/app/shared/ui/dataTableUtils';
import PaginatedList from '@/app/shared/ui/PaginatedList';
import { TableEmptyState } from '@/app/shared/ui/TableEmptyState';

export type DataTableColumn<T> = {
  id: string;
  header: ReactNode;
  cell?: (row: T) => ReactNode;
  mobileLabel?: string;
};

export type DataTablePagination = {
  desktop: number;
  mobile: number;
  initialPage?: number;
  mobileMaxWidth?: number;
};

export type DataTableProps<T> = {
  columns?: DataTableColumn<T>[];
  headers?: ReactNode[];
  data: T[];
  getRowKey: (row: T) => string;
  desktopGridClassName: string;
  desktopMinWidth?: number;
  emptyText?: string;
  pagination?: false | DataTablePagination;
  headerTrailing?: ReactNode;
  rowTrailing?: (row: T) => ReactNode;
  renderRow?: (row: T) => ReactNode;
  renderMobile?: (row: T) => ReactNode;
  renderDesktop?: (row: T) => ReactNode;
  className?: string;
  listClassName?: string;
  headerClassName?: string;
  headerCellClassName?: string;
  rowClassName?: string;
  desktopRowClassName?: string;
  mobileRowClassName?: string;
  mobileFieldsClassName?: string;
};

const desktopBreakpoint = (desktopMinWidth: number) => {
  if (desktopMinWidth === 768) {
    return {
      gap: 'md:gap-0',
      hidden: 'md:hidden',
      show: 'hidden md:grid',
      contents: 'md:contents',
      rowPad: 'md:px-5 md:py-4',
    } as const;
  }

  if (desktopMinWidth === 950) {
    return {
      gap: 'min-[950px]:gap-0',
      hidden: 'min-[950px]:hidden',
      show: 'hidden min-[950px]:grid',
      contents: 'min-[950px]:contents',
      rowPad: 'min-[950px]:px-5 min-[950px]:py-4',
    } as const;
  }

  return {
    gap: 'min-[960px]:gap-0',
    hidden: 'min-[960px]:hidden',
    show: 'hidden min-[960px]:grid',
    contents: 'min-[960px]:contents',
    rowPad: 'min-[960px]:px-5 min-[960px]:py-4',
  } as const;
};

const DataTableField = ({
  label,
  desktopMinWidth,
  children,
}: {
  label?: string;
  desktopMinWidth: number;
  children: ReactNode;
}) => {
  const bp = desktopBreakpoint(desktopMinWidth);

  return (
    <div className={cn('flex min-w-0 flex-col gap-2', bp.gap)}>
      {label != null && (
        <span className={cn('text-sm font-medium tracking-[0.075em] text-nowrap text-gray-500', bp.hidden)}>
          {label}
        </span>
      )}
      {children}
    </div>
  );
};

export const DataTableValue = ({
  value,
  className,
}: {
  value: ReactNode;
  className?: string;
}) => (
  <span className={cn('min-w-0 truncate text-base', dataTableValueClassName(value), className)}>{value}</span>
);

export const DataTableDollar = ({ value }: { value: string }) => {
  const amount = value.startsWith('$') ? value.slice(1) : value;
  const isEmpty = isEmptyTableValue(value) || isEmptyTableValue(amount);

  return (
    <span className={cn('min-w-0 truncate text-base', isEmpty ? 'text-gray-500' : 'text-gray-200')}>
      {value.startsWith('$') && !isEmpty && <span className='text-gray-500'>$</span>}
      {amount}
    </span>
  );
};

export const DataTableStacked = ({
  primary,
  secondary,
  secondaryClassName = 'text-sm text-gray-400',
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  secondaryClassName?: string;
}) => (
  <div className='flex min-w-0 flex-col gap-0.5'>
    <span className={cn('truncate text-base', dataTableValueClassName(primary))}>{primary}</span>
    {secondary != null && (
      <span className={cn('truncate', isEmptyTableValue(secondary) ? 'text-sm text-gray-500' : secondaryClassName)}>
        {secondary}
      </span>
    )}
  </div>
);

const DataTable = <T,>({
  columns = [],
  headers,
  data,
  getRowKey,
  desktopGridClassName,
  desktopMinWidth = 960,
  emptyText,
  pagination = { desktop: 5, mobile: 2 },
  headerTrailing,
  rowTrailing,
  renderRow,
  renderMobile,
  renderDesktop,
  className,
  listClassName = 'gap-2',
  headerClassName,
  headerCellClassName = 'min-w-0 truncate text-sm font-medium tracking-[0.075em] text-nowrap text-gray-500',
  rowClassName,
  desktopRowClassName,
  mobileRowClassName,
  mobileFieldsClassName = 'gap-x-4 gap-y-[21px]',
}: DataTableProps<T>) => {
  const bp = desktopBreakpoint(desktopMinWidth);
  const mobileMax = pagination === false ? desktopMinWidth : (pagination.mobileMaxWidth ?? desktopMinWidth);
  const isMobileViewport = useScreenWidth() < mobileMax;
  const splitLayout = renderMobile != null && renderRow == null;
  const headerLabels = headers ?? columns.map((column) => column.header);

  const header = (
    <div className={cn(desktopGridClassName, bp.show, headerClassName)}>
      {headerLabels.map((label, index) => (
        <span key={typeof label === 'string' ? label : index} className={headerCellClassName}>
          {label}
        </span>
      ))}
      {headerTrailing}
    </div>
  );

  const renderUnifiedRow = (row: T) => (
    <div
      key={getRowKey(row)}
      className={cn(
        desktopGridClassName,
        'rounded-md bg-elevated-card-2 p-4 font-normal hover:bg-elevated-card-hover',
        bp.rowPad,
        rowClassName,
        desktopRowClassName,
      )}
    >
      <div className={cn('grid grid-cols-2', mobileFieldsClassName, bp.contents)}>
        {columns.map((column) => (
          <DataTableField
            key={column.id}
            label={column.mobileLabel ?? (typeof column.header === 'string' ? column.header : undefined)}
            desktopMinWidth={desktopMinWidth}
          >
            {column.cell?.(row)}
          </DataTableField>
        ))}
        {rowTrailing?.(row)}
      </div>
    </div>
  );

  const renderSplitRow = (row: T) => (
    <div key={getRowKey(row)}>
      <div
        className={cn(
          'rounded-md bg-elevated-card-2 font-normal hover:bg-elevated-card-hover',
          bp.hidden,
          mobileRowClassName,
          rowClassName,
        )}
      >
        {renderMobile!(row)}
      </div>
      <div
        className={cn(
          desktopGridClassName,
          bp.show,
          'rounded-md bg-elevated-card-2 font-normal hover:bg-elevated-card-hover',
          desktopRowClassName,
          rowClassName,
        )}
      >
        {renderDesktop?.(row) ?? (
          <>
            {columns.map((column) => (
              <div key={column.id} className='min-w-0'>
                {column.cell?.(row)}
              </div>
            ))}
            {rowTrailing?.(row)}
          </>
        )}
      </div>
    </div>
  );

  const rows = data.map((row) => {
    if (renderRow) return <Fragment key={getRowKey(row)}>{renderRow(row)}</Fragment>;
    if (splitLayout) return renderSplitRow(row);
    return renderUnifiedRow(row);
  });

  const list =
    data.length === 0 && emptyText != null ? (
      <TableEmptyState text={emptyText} />
    ) : pagination === false ? (
      <div className={cn('flex flex-col', listClassName)}>{rows}</div>
    ) : (
      <PaginatedList
        itemsPerPage={isMobileViewport ? pagination.mobile : pagination.desktop}
        initialPage={pagination.initialPage}
        className={listClassName}
        paginationClassName='mt-3 pt-0'
      >
        {rows}
      </PaginatedList>
    );

  return (
    <div className={cn('flex min-w-0 flex-col gap-3', className)}>
      {header}
      {list}
    </div>
  );
};

export default DataTable;
